import { FirebaseApp, initializeApp } from "firebase/app";
import {
  Auth,
  connectAuthEmulator,
  getAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  User,
} from "firebase/auth";

let app: FirebaseApp;
let auth: Auth;

/**
 * Get Firebase config from runtime injection or environment variables
 */
function getFirebaseConfig() {
  // In production, config is injected at runtime via config.js
  const runtimeConfig = window.FIREBASE_CONFIG;

  if (runtimeConfig) {
    console.log("🔧 Using runtime Firebase config");
    return runtimeConfig;
  }

  // Fallback to environment variables for local development
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-nomato";
  console.log("🔧 Using Firebase config from environment variables");

  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "fake-api-key",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || `${projectId}.firebaseapp.com`,
    projectId,
  };
}

/**
 * Initialize Firebase with environment configuration
 */
export function initializeFirebase(): { app: FirebaseApp; auth: Auth } {
  if (app && auth) {
    return { app, auth };
  }

  const firebaseConfig = getFirebaseConfig();

  app = initializeApp(firebaseConfig);
  auth = getAuth(app);

  // Connect to emulator in development (must be done before any auth operations)
  const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;

  if (emulatorHost) {
    const [host, port] = emulatorHost.split(":");
    connectAuthEmulator(auth, `http://${host}:${port}`, {
      disableWarnings: true,
    });
    console.log(`🔧 Connected to Firebase Auth Emulator at ${host}:${port}`);
  }

  return { app, auth };
}

/**
 * Get Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
  if (!auth) {
    initializeFirebase();
  }
  return auth;
}

/**
 * Get current Firebase user
 */
export function getCurrentUser(): User | null {
  return getFirebaseAuth().currentUser;
}

/**
 * Listen to auth state changes
 */
export function onAuthChanged(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(getFirebaseAuth(), callback);
}

/**
 * Check if URL is a sign-in link
 */
export function isEmailSignInLink(url: string): boolean {
  return isSignInWithEmailLink(getFirebaseAuth(), url);
}

/**
 * Sign in with email link
 */
export async function signInWithEmail(email: string, emailLink: string): Promise<User> {
  const userCredential = await signInWithEmailLink(getFirebaseAuth(), email, emailLink);
  return userCredential.user;
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  await firebaseSignOut(getFirebaseAuth());
}

/**
 * Get Firebase ID token for authenticated requests
 */
export async function getIdToken(): Promise<string | null> {
  const user = getCurrentUser();
  if (!user) return null;
  return await user.getIdToken();
}
